import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Settings2, Download, CheckCircle2, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import {
  bonificacionListReglas, bonificacionCrearRegla, bonificacionGenerarFichero,
  bonificacionListFicheros, bonificacionAprobarFichero, bonificacionDescargarFichero,
  bonificacionSubirRetorno, bonificacionDescargarInformePdf,
} from "@/api";
import IndicadoresBonificacion from "./IndicadoresBonificacion";
import AuditoriaBonificacion from "./AuditoriaBonificacion";

export default function BonificacionPanel() {
  const [reglas, setReglas] = useState([]);
  const [ficheros, setFicheros] = useState([]);
  const [periodo, setPeriodo] = useState(new Date().toISOString().slice(0, 7));
  const [generando, setGenerando] = useState(false);
  const [showReglaDialog, setShowReglaDialog] = useState(false);
  const [nuevaRegla, setNuevaRegla] = useState({ tipo: "visitas_anio", valor_n: 4, valor_m: "", descripcion: "" });
  const [savingRegla, setSavingRegla] = useState(false);
  const [uploadingRetorno, setUploadingRetorno] = useState(false);

  const load = () => {
    bonificacionListReglas().then(setReglas).catch(() => {});
    bonificacionListFicheros().then(setFicheros).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const reglaVigente = reglas.find((r) => r.activa);

  const handleGenerar = async () => {
    setGenerando(true);
    try {
      await bonificacionGenerarFichero(periodo);
      toast.success(`Fichero de ${periodo} generado (borrador)`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo generar el fichero"));
    } finally {
      setGenerando(false);
    }
  };

  const handleAprobar = async (id) => {
    try {
      await bonificacionAprobarFichero(id);
      toast.success("Fichero aprobado");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo aprobar"));
    }
  };

  const handleCrearRegla = async () => {
    setSavingRegla(true);
    try {
      await bonificacionCrearRegla({
        tipo: nuevaRegla.tipo,
        valor_n: Number(nuevaRegla.valor_n),
        valor_m: nuevaRegla.valor_m ? Number(nuevaRegla.valor_m) : null,
        descripcion: nuevaRegla.descripcion,
      });
      toast.success("Nueva versión de regla creada");
      setShowReglaDialog(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo crear la regla"));
    } finally {
      setSavingRegla(false);
    }
  };

  const handleRetorno = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingRetorno(true);
    try {
      const res = await bonificacionSubirRetorno(file);
      toast.success(`Fichero de retorno procesado: ${res.actualizados} filas`);
    } catch {
      toast.error("No se pudo procesar el fichero de retorno");
    } finally {
      setUploadingRetorno(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="bonificacion-panel" className="space-y-8">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-semibold text-municipal flex items-center gap-2"><Settings2 className="w-4 h-4" /> Regla de bonificación vigente</h3>
          <Button size="sm" variant="outline" className="border-municipal text-municipal" onClick={() => setShowReglaDialog(true)} data-testid="nueva-regla-btn">
            Nueva versión
          </Button>
        </div>
        {reglaVigente ? (
          <div className="text-sm text-gray-600" data-testid="regla-vigente-texto">
            <Badge className="mr-2">v{reglaVigente.version}</Badge>
            {reglaVigente.tipo === "visitas_anio"
              ? `Mínimo ${reglaVigente.valor_n} visitas computables al año`
              : `Mínimo ${reglaVigente.valor_m} meses distintos con visita (y ${reglaVigente.valor_n} visitas/año)`}
            {reglaVigente.descripcion && <span className="block text-xs text-amber-600 mt-1">{reglaVigente.descripcion}</span>}
          </div>
        ) : <p className="text-sm text-gray-400">Sin regla configurada</p>}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-heading font-semibold text-municipal flex items-center gap-2 mb-4"><FileText className="w-4 h-4" /> Ficheros mensuales (A/B/C)</h3>
        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Periodo</Label>
            <Input type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} data-testid="periodo-input" />
          </div>
          <Button onClick={handleGenerar} disabled={generando} className="bg-municipal hover:bg-municipal-dark text-white" data-testid="generar-fichero-btn">
            {generando && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Generar fichero del mes
          </Button>
          <label className="inline-flex">
            <Button type="button" variant="outline" className="border-municipal text-municipal" disabled={uploadingRetorno} asChild>
              <span><Upload className="w-4 h-4 mr-2" /> Subir fichero de retorno</span>
            </Button>
            <input type="file" accept=".csv" className="hidden" onChange={handleRetorno} data-testid="retorno-upload-input" />
          </label>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Periodo</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>DNI / Cumplen</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ficheros.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-gray-400 py-8">Sin ficheros generados.</TableCell></TableRow>
            )}
            {ficheros.map((f) => (
              <TableRow key={f.id} data-testid={`fichero-row-${f.periodo}`}>
                <TableCell className="font-medium text-municipal">{f.periodo}</TableCell>
                <TableCell>
                  <Badge variant={f.estado === "aprobado" ? "default" : "outline"} className={f.estado === "aprobado" ? "bg-valoriza-green" : ""}>
                    {f.estado}
                  </Badge>
                </TableCell>
                <TableCell>{f.total_dni} / {f.total_cumple}</TableCell>
                <TableCell className="text-right space-x-1">
                  {f.estado === "borrador" && (
                    <Button size="sm" variant="outline" className="border-valoriza-green text-valoriza-green" onClick={() => handleAprobar(f.id)} data-testid={`aprobar-btn-${f.periodo}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {["A", "B", "C"].map((tipo) => (
                    <Button key={tipo} size="sm" variant="ghost" onClick={() => bonificacionDescargarFichero(f.id, tipo, f.periodo)} data-testid={`descargar-${tipo}-${f.periodo}`}>
                      <Download className="w-3 h-3 mr-1" />{tipo}
                    </Button>
                  ))}
                  <Button size="sm" variant="ghost" className="text-municipal" onClick={() => bonificacionDescargarInformePdf(f.id, f.periodo)} data-testid={`descargar-pdf-${f.periodo}`}>
                    <FileText className="w-3 h-3 mr-1" />PDF
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <IndicadoresBonificacion />

      <AuditoriaBonificacion />

      <Dialog open={showReglaDialog} onOpenChange={setShowReglaDialog}>
        <DialogContent className="max-w-md" data-testid="nueva-regla-dialog">
          <DialogHeader><DialogTitle className="text-municipal font-heading">Nueva versión de la regla</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Mínimo de visitas al año</Label>
              <Input type="number" min="1" value={nuevaRegla.valor_n} onChange={(e) => setNuevaRegla({ ...nuevaRegla, valor_n: e.target.value })} data-testid="regla-valor-n-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Descripción / referencia a la ordenanza (opcional)</Label>
              <Input value={nuevaRegla.descripcion} onChange={(e) => setNuevaRegla({ ...nuevaRegla, descripcion: e.target.value })} data-testid="regla-descripcion-input" />
            </div>
            <Button onClick={handleCrearRegla} disabled={savingRegla} className="w-full bg-municipal hover:bg-municipal-dark text-white" data-testid="guardar-regla-btn">
              {savingRegla && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Guardar nueva versión
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
